import { createFileRoute } from '@tanstack/react-router';
import { useState, useEffect, useCallback } from 'react';
import * as Sentry from '@sentry/react';
import { api } from '../../utils/api';
import { formatDate } from '../../utils/dateUtils';
import { usePermissions } from '../../hooks/usePermissions';
import { NavigationSidebar } from '../../components/layout/NavigationSidebar';
import { PageContainer } from '../../components/layout/PageContainer';

export const Route = createFileRoute('/platform-admin/')({
  component: PlatformAdminPage,
});

interface PlatformStats {
  totalCompanies: number;
  totalUsers: number;
  activeUsers: number;
  totalAssets: number;
  totalRisks: number;
  totalBusinessRisks: number;
}

interface CompanyItem {
  id: string;
  name: string;
  slug: string;
  isActive: boolean;
  userCount: number;
  createdAt: string;
}

function PlatformAdminPage() {
  const { isSuperAdmin } = usePermissions();
  const [stats, setStats] = useState<PlatformStats | null>(null);
  const [companies, setCompanies] = useState<CompanyItem[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  const loadData = useCallback(async () => {
    try {
      setLoading(true);
      const [statsData, companiesData] = await Promise.all([
        api.get('/super/stats') as Promise<PlatformStats>,
        api.get('/super/companies') as Promise<CompanyItem[]>,
      ]);
      setStats(statsData);
      setCompanies(companiesData);
    } catch (err) {
      setError((err as Error).message);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    if (isSuperAdmin) loadData();
  }, [isSuperAdmin, loadData]);

  async function handleToggleCompany(companyId: string, isActive: boolean) {
    try {
      await api.put(`/super/companies/${companyId}`, { isActive: !isActive });
      loadData();
    } catch (err) {
      alert((err as Error).message);
    }
  }

  async function handleDeleteCompany(companyId: string, companyName: string) {
    const confirmed = window.confirm(
      `Are you sure you want to permanently delete "${companyName}" and ALL its data? This action cannot be undone.`
    );
    if (!confirmed) return;

    try {
      await api.del(`/super/companies/${companyId}`);
      loadData();
    } catch (err) {
      alert((err as Error).message);
    }
  }

  if (!isSuperAdmin) {
    return (
      <div className="flex flex-1 overflow-auto">
        <NavigationSidebar />
        <PageContainer>
          <div className="p-6">
            <h1 className="text-2xl font-bold text-gray-900 mb-2">Platform Administration</h1>
            <p className="text-gray-500">This page is only accessible to super administrators.</p>
          </div>
        </PageContainer>
      </div>
    );
  }

  return (
    <div className="flex flex-1 overflow-auto">
      <NavigationSidebar />
      <PageContainer>
        <div className="p-6 space-y-6">
          <h1 className="text-2xl font-bold text-gray-900">Platform Administration</h1>
          <p className="text-gray-500">Manage all companies and view platform-wide statistics</p>

          {error && (
            <div className="bg-red-50 text-red-700 px-4 py-3 rounded-lg text-sm">{error}</div>
          )}

          {loading ? (
            <div className="text-center py-12">
              <div className="inline-block w-8 h-8 border-4 border-purple-200 border-t-purple-600 rounded-full animate-spin" />
            </div>
          ) : (
            <>
              {/* Stats cards */}
              {stats && (
                <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                  <StatCard label="Companies" value={stats.totalCompanies} colour="bg-purple-100 text-purple-700" />
                  <StatCard label="Users" value={stats.totalUsers} colour="bg-blue-100 text-blue-700" />
                  <StatCard label="Assets" value={stats.totalAssets} colour="bg-green-100 text-green-700" />
                  <StatCard label="Risks" value={stats.totalRisks + stats.totalBusinessRisks} colour="bg-red-100 text-red-700" />
                </div>
              )}

              {/* Companies table */}
              <div className="bg-white rounded-xl border border-gray-200 overflow-hidden">
                <div className="px-6 py-4 border-b border-gray-200">
                  <h2 className="text-lg font-semibold text-gray-900">Companies ({companies.length})</h2>
                </div>
                <div className="overflow-x-auto">
                  <table className="w-full">
                    <thead className="bg-gray-50">
                      <tr>
                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Name</th>
                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Slug</th>
                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Users</th>
                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Status</th>
                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Created</th>
                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Actions</th>
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-gray-200">
                      {companies.map((c) => (
                        <tr key={c.id} className="hover:bg-gray-50">
                          <td className="px-6 py-4 text-sm font-medium text-gray-900">{c.name}</td>
                          <td className="px-6 py-4 text-sm text-gray-500 font-mono">{c.slug}</td>
                          <td className="px-6 py-4 text-sm text-gray-500">{c.userCount}</td>
                          <td className="px-6 py-4">
                            <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${
                              c.isActive ? 'bg-green-100 text-green-700' : 'bg-red-100 text-red-700'
                            }`}>
                              {c.isActive ? 'Active' : 'Deactivated'}
                            </span>
                          </td>
                          <td className="px-6 py-4 text-sm text-gray-500">
                            {formatDate(c.createdAt)}
                          </td>
                          <td className="px-6 py-4">
                            <div className="flex gap-2">
                              <button
                                onClick={() => handleToggleCompany(c.id, c.isActive)}
                                className={`text-xs font-medium px-3 py-1 rounded ${
                                  c.isActive
                                    ? 'text-amber-600 hover:bg-amber-50'
                                    : 'text-green-600 hover:bg-green-50'
                                }`}
                              >
                                {c.isActive ? 'Deactivate' : 'Activate'}
                              </button>
                              <button
                                onClick={() => handleDeleteCompany(c.id, c.name)}
                                className="text-xs font-medium px-3 py-1 rounded text-red-600 hover:bg-red-50"
                              >
                                Delete
                              </button>
                            </div>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>

              {/* Native-auth cutover panel — super-admin only */}
              <CutoverPanel />

              {/* Sentry smoke-test panel — super-admin only, permanent */}
              <SentrySmokeTestPanel />
            </>
          )}
        </div>
      </PageContainer>
    </div>
  );
}

// ════════════════════════════════════════════════════════════════════════
// Native-auth cutover panel — Playbook PR 2
//
// Super-admin only. Shows a live readiness count (active users + how many
// have phones) and exposes the bulk cutover endpoint behind a typed-YES
// confirmation modal.
// ════════════════════════════════════════════════════════════════════════
interface CutoverReadiness {
  activeUsers: number;
  usersWithPhone: number;
  usersWithoutPhone: number;
}

interface CutoverResult {
  message: string;
  sent: number;
  failed: number;
  totalActiveUsers: number;
}

function CutoverPanel() {
  const [authConfig, setAuthConfig] = useState<{ enhancedAuthEnabled: boolean; smsEnabled: boolean } | null>(null);
  const [readiness, setReadiness] = useState<CutoverReadiness | null>(null);
  const [readinessError, setReadinessError] = useState('');
  const [showConfirm, setShowConfirm] = useState(false);
  const [confirmText, setConfirmText] = useState('');
  const [running, setRunning] = useState(false);
  const [result, setResult] = useState<CutoverResult | null>(null);
  const [error, setError] = useState('');

  const reload = useCallback(async () => {
    try {
      const [config, r] = await Promise.all([
        fetch('/api/auth/config').then((res) => res.json()) as Promise<{ enhancedAuthEnabled: boolean; smsEnabled: boolean }>,
        api.get('/users/cutover-readiness') as Promise<CutoverReadiness>,
      ]);
      setAuthConfig(config);
      setReadiness(r);
    } catch (err) {
      setReadinessError((err as Error).message);
    }
  }, []);

  useEffect(() => { reload(); }, [reload]);

  async function fireCutover() {
    setRunning(true);
    setError('');
    try {
      const data = await api.post('/users/send-cutover-reset-to-all', { confirm: 'YES' }) as CutoverResult;
      setResult(data);
      setShowConfirm(false);
      setConfirmText('');
      reload();
    } catch (err) {
      setError((err as Error).message);
    } finally {
      setRunning(false);
    }
  }

  const phoneCoverage = readiness && readiness.activeUsers > 0
    ? Math.round((readiness.usersWithPhone / readiness.activeUsers) * 100)
    : null;

  return (
    <div className="bg-white rounded-xl border border-gray-200">
      <div className="px-6 py-4 border-b border-gray-200">
        <h2 className="text-lg font-semibold text-gray-900">Native-auth cutover</h2>
        <p className="text-sm text-gray-500 mt-1">
          Migrate users to the new password + SMS-OTP sign-in flow. Permanent infrastructure for this company —
          super-admin only. Read{' '}
          <a
            href="https://github.com/AndyWard-Dev/asset-inventory-audit-soa/blob/main/Project%20Status/DESIGN_AND_FEATURES.md"
            target="_blank"
            rel="noreferrer"
            className="text-purple-700 underline"
          >
            DESIGN_AND_FEATURES
          </a>{' '}
          for the full procedure.
        </p>
      </div>

      <div className="p-6 space-y-4">
        {/* Flag + SMS status */}
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
          <div className={`rounded-lg border px-4 py-3 ${authConfig?.enhancedAuthEnabled ? 'border-green-200 bg-green-50' : 'border-gray-200 bg-gray-50'}`}>
            <div className="text-xs uppercase tracking-wide text-gray-500">Enhanced auth flag</div>
            <div className="font-medium text-gray-900 mt-1">
              {authConfig === null ? 'Loading…' : authConfig.enhancedAuthEnabled ? 'ON — new flow live' : 'OFF — legacy flow live'}
            </div>
            <div className="text-xs text-gray-500 mt-1">Set <code className="font-mono">ENHANCED_AUTH_ENABLED</code> on the backend component.</div>
          </div>
          <div className={`rounded-lg border px-4 py-3 ${authConfig?.smsEnabled ? 'border-green-200 bg-green-50' : 'border-amber-200 bg-amber-50'}`}>
            <div className="text-xs uppercase tracking-wide text-gray-500">SMS delivery</div>
            <div className="font-medium text-gray-900 mt-1">
              {authConfig === null ? 'Loading…' : authConfig.smsEnabled ? 'ON — Twilio configured' : 'OFF — codes will only be logged'}
            </div>
            <div className="text-xs text-gray-500 mt-1">Set <code className="font-mono">TWILIO_ENABLED=true</code> + credentials.</div>
          </div>
        </div>

        {/* Readiness */}
        {readinessError ? (
          <div className="bg-red-50 text-red-700 px-4 py-3 rounded-lg text-sm">{readinessError}</div>
        ) : readiness === null ? (
          <div className="text-sm text-gray-500">Loading readiness…</div>
        ) : (
          <div className="rounded-lg border border-gray-200 px-4 py-3">
            <div className="flex items-center justify-between mb-2">
              <div className="text-sm font-medium text-gray-700">Phone coverage in this company</div>
              <div className="text-sm font-mono">
                <span className="font-medium">{readiness.usersWithPhone}</span>
                <span className="text-gray-400"> / </span>
                <span>{readiness.activeUsers}</span>
                {phoneCoverage !== null && <span className="ml-2 text-gray-500">({phoneCoverage}%)</span>}
              </div>
            </div>
            <div className="h-2 rounded-full bg-gray-200 overflow-hidden">
              <div
                className={`h-full ${
                  phoneCoverage === 100 ? 'bg-green-500' : phoneCoverage && phoneCoverage >= 50 ? 'bg-amber-500' : 'bg-red-500'
                }`}
                style={{ width: `${phoneCoverage ?? 0}%` }}
              />
            </div>
            {readiness.usersWithoutPhone > 0 && (
              <p className="text-xs text-amber-700 mt-2">
                <strong>{readiness.usersWithoutPhone}</strong> active user{readiness.usersWithoutPhone === 1 ? '' : 's'} have no phone on file.
                They will not be able to sign in once the flag is flipped — set their phones via User Management → ⋯ → "Set phone number" first.
              </p>
            )}
          </div>
        )}

        {/* Action */}
        <div>
          <h3 className="text-sm font-medium text-gray-700 mb-2">Bulk reset</h3>
          <p className="text-xs text-gray-500 mb-3">
            Sends a password-reset email to every active user in this company and flags them to set a new password on next sign-in.
            Idempotent — safe to re-fire if some users haven't actioned theirs.
          </p>
          <button
            onClick={() => setShowConfirm(true)}
            disabled={running || !readiness}
            className="px-4 py-2 text-sm text-white font-medium rounded-lg disabled:opacity-50 bg-amber-600 hover:bg-amber-700"
          >
            Send cutover reset email to all users
          </button>
        </div>

        {/* Last result */}
        {result && (
          <div className="rounded-lg border border-green-200 bg-green-50 px-4 py-3 text-sm">
            <div className="text-green-700 font-medium">{result.message}</div>
            <div className="text-xs text-gray-600 mt-1">
              Sent: {result.sent} · Failed: {result.failed} · Total active users: {result.totalActiveUsers}
            </div>
          </div>
        )}
        {error && (
          <div className="bg-red-50 text-red-700 px-4 py-3 rounded-lg text-sm">{error}</div>
        )}
      </div>

      {showConfirm && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50">
          <div className="bg-white rounded-xl shadow-xl w-full max-w-md p-6">
            <h3 className="text-lg font-semibold text-gray-900 mb-2">Confirm bulk reset</h3>
            <p className="text-sm text-gray-600 mb-4">
              This will email a reset link to every active user in your company and flag them to change their password on next sign-in.
              Type <span className="font-mono font-bold">YES</span> below to confirm.
            </p>
            <input
              type="text"
              autoFocus
              value={confirmText}
              onChange={(e) => setConfirmText(e.target.value)}
              placeholder="Type YES to confirm"
              className="w-full px-4 py-2.5 border border-gray-300 rounded-lg focus:ring-2 focus:ring-amber-500 focus:border-amber-500 outline-none font-mono"
            />
            <div className="flex justify-end gap-3 mt-5">
              <button
                type="button"
                disabled={running}
                onClick={() => { setShowConfirm(false); setConfirmText(''); setError(''); }}
                className="px-4 py-2 text-sm text-gray-600 hover:text-gray-900 disabled:opacity-50"
              >
                Cancel
              </button>
              <button
                type="button"
                disabled={running || confirmText !== 'YES'}
                onClick={fireCutover}
                className="px-4 py-2 text-sm text-white font-medium rounded-lg disabled:opacity-50 bg-amber-600 hover:bg-amber-700"
              >
                {running ? 'Sending…' : 'Send reset emails'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

interface SmokeTestResult {
  source: 'backend' | 'frontend';
  type: string;
  ok: boolean;
  eventId: string | null;
  note: string;
  status: number | null;
  ranAt: string;
}

function SentrySmokeTestPanel() {
  const [results, setResults] = useState<SmokeTestResult[]>([]);
  const [running, setRunning] = useState<string | null>(null);

  function record(r: SmokeTestResult) {
    setResults((prev) => [r, ...prev].slice(0, 6));
  }

  async function callBackend(path: string, label: string) {
    setRunning(label);
    const ranAt = new Date().toISOString();
    try {
      const data = await api.post(`/_sentry-test/${path}`, {}) as {
        ok: boolean; type: string; eventId: string | null; note: string;
      };
      record({
        source: 'backend',
        type: data.type ?? path,
        ok: !!data.ok,
        eventId: data.eventId ?? null,
        note: data.note ?? '',
        status: 200,
        ranAt,
      });
    } catch (err) {
      const e = err as Error & { status?: number };
      // For /throw the server returns 500 by design — that's success for the smoke test.
      const isExpectedThrow = path === 'throw' && e.status === 500;
      record({
        source: 'backend',
        type: isExpectedThrow ? 'unhandled-throw' : path,
        ok: isExpectedThrow,
        eventId: null,
        note: isExpectedThrow
          ? 'Server returned 500 as expected — the unhandled throw flowed through Express. Check Sentry for the captured event (the SDK does not return the event id over HTTP for thrown errors).'
          : `Request failed: ${e.message}`,
        status: e.status ?? null,
        ranAt,
      });
    } finally {
      setRunning(null);
    }
  }

  function fireFrontendMessage() {
    setRunning('frontend-message');
    const ranAt = new Date().toISOString();
    const eventId = Sentry.captureMessage(
      `Sentry frontend smoke test (captureMessage) at ${ranAt}`,
      'info'
    );
    record({
      source: 'frontend',
      type: 'captureMessage',
      ok: !!eventId,
      eventId: eventId ?? null,
      note: eventId
        ? 'Message sent to Sentry from the browser. Look for it in the frontend project as an "info" event.'
        : 'Sentry SDK returned no event id — VITE_SENTRY_DSN may be unset, or the static-site rebuild has not picked up the value yet.',
      status: null,
      ranAt,
    });
    setRunning(null);
  }

  function fireFrontendException() {
    setRunning('frontend-exception');
    const ranAt = new Date().toISOString();
    const eventId = Sentry.captureException(
      new Error('Sentry frontend smoke test (captureException)')
    );
    record({
      source: 'frontend',
      type: 'captureException',
      ok: !!eventId,
      eventId: eventId ?? null,
      note: eventId
        ? 'Exception sent to Sentry as a deliberate test.'
        : 'Sentry SDK returned no event id — VITE_SENTRY_DSN may be unset.',
      status: null,
      ranAt,
    });
    setRunning(null);
  }

  return (
    <div className="bg-white rounded-xl border border-gray-200">
      <div className="px-6 py-4 border-b border-gray-200">
        <h2 className="text-lg font-semibold text-gray-900">Sentry smoke test</h2>
        <p className="text-sm text-gray-500 mt-1">
          Fire deliberate test events into Sentry to verify the wiring. Permanent infrastructure —
          super-admin only. Events appear in your{' '}
          <a
            href="https://andy-ward-services.sentry.io/issues/"
            target="_blank"
            rel="noreferrer"
            className="text-purple-700 underline"
          >
            andy-ward-services
          </a>{' '}
          Sentry org within ~30 seconds; search by the event id shown below.
        </p>
      </div>

      <div className="p-6 space-y-4">
        {/* Backend buttons */}
        <div>
          <h3 className="text-sm font-medium text-gray-700 mb-2">Backend</h3>
          <div className="flex flex-wrap gap-2">
            <button
              onClick={() => callBackend('message', 'backend-message')}
              disabled={running !== null}
              className="px-3 py-1.5 text-sm border border-gray-300 rounded-lg hover:bg-gray-50 disabled:opacity-50"
            >
              Send captureMessage
            </button>
            <button
              onClick={() => callBackend('exception', 'backend-exception')}
              disabled={running !== null}
              className="px-3 py-1.5 text-sm border border-gray-300 rounded-lg hover:bg-gray-50 disabled:opacity-50"
            >
              Send captureException
            </button>
            <button
              onClick={() => callBackend('throw', 'backend-throw')}
              disabled={running !== null}
              className="px-3 py-1.5 text-sm border border-amber-300 text-amber-800 rounded-lg hover:bg-amber-50 disabled:opacity-50"
            >
              Trigger unhandled throw (Express path)
            </button>
          </div>
        </div>

        {/* Frontend buttons */}
        <div>
          <h3 className="text-sm font-medium text-gray-700 mb-2">Frontend</h3>
          <div className="flex flex-wrap gap-2">
            <button
              onClick={fireFrontendMessage}
              disabled={running !== null}
              className="px-3 py-1.5 text-sm border border-gray-300 rounded-lg hover:bg-gray-50 disabled:opacity-50"
            >
              Send captureMessage
            </button>
            <button
              onClick={fireFrontendException}
              disabled={running !== null}
              className="px-3 py-1.5 text-sm border border-gray-300 rounded-lg hover:bg-gray-50 disabled:opacity-50"
            >
              Send captureException
            </button>
          </div>
        </div>

        {/* Results log */}
        {results.length > 0 && (
          <div className="border-t border-gray-100 pt-4">
            <h3 className="text-sm font-medium text-gray-700 mb-2">Recent test events</h3>
            <ul className="space-y-2">
              {results.map((r, idx) => (
                <li
                  key={`${r.ranAt}-${idx}`}
                  className={`text-sm rounded-lg border px-3 py-2 ${
                    r.ok ? 'border-green-200 bg-green-50' : 'border-red-200 bg-red-50'
                  }`}
                >
                  <div className="flex items-center gap-2 flex-wrap">
                    <span className={r.ok ? 'text-green-700' : 'text-red-700'}>
                      {r.ok ? '✓' : '✗'}
                    </span>
                    <span className="font-medium text-gray-900 capitalize">
                      {r.source} · {r.type}
                    </span>
                    {r.eventId && (
                      <span className="font-mono text-xs text-gray-600 bg-white px-2 py-0.5 rounded border border-gray-200">
                        event {r.eventId}
                      </span>
                    )}
                  </div>
                  <p className="text-xs text-gray-600 mt-1">{r.note}</p>
                </li>
              ))}
            </ul>
          </div>
        )}
      </div>
    </div>
  );
}

function StatCard({ label, value, colour }: { label: string; value: number; colour: string }) {
  return (
    <div className="bg-white rounded-xl border border-gray-200 p-5">
      <p className="text-sm text-gray-500 mb-1">{label}</p>
      <p className="text-2xl font-bold text-gray-900">{(value ?? 0).toLocaleString()}</p>
      <div className={`inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium mt-2 ${colour}`}>
        Total
      </div>
    </div>
  );
}
